/**
 * Page des To Dos d'un contact : affiche les todos de tous les utilisateurs.
 */

import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getContact, getContactTodos } from "../api/contactApi";
import { deleteTodo } from "../api/todoApi";
import { useAuth } from "../contexts/AuthContext";
import SortableTable from "../components/SortableTable";
import StatusBadge from "../components/StatusBadge";
import ConfirmDialog from "../components/ConfirmDialog";
import { formatDate, isOverdue } from "../utils/dateUtils";
import { STATUS_LABELS, STAKE_LABELS, URGENCY_LABELS } from "../utils/constants";
import "./PageStyles.css";

export default function ContactTodosPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contact, setContact] = useState(null);
  const [todos, setTodos] = useState([]);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    loadData();
  }, [id]);

  async function loadData() {
    const [contactData, todosData] = await Promise.all([
      getContact(id),
      getContactTodos(id),
    ]);
    setContact(contactData);
    setTodos(todosData);
  }

  async function handleDelete() {
    await deleteTodo(deleteId);
    setDeleteId(null);
    loadData();
  }

  const isOwner = contact && contact.owner_id === user?.id;
  const fullName = contact
    ? `${contact.first_name || ""} ${contact.last_name || ""}`.trim()
    : "";

  const columns = [
    { key: "owner_name", label: "Créateur" },
    { key: "description", label: "Description" },
    { key: "responsable_name", label: "Responsable" },
    { key: "due_date", label: "Échéance" },
    { key: "stake_level", label: "Enjeu" },
    { key: "urgency_level", label: "Urgence" },
    { key: "status", label: "Statut" },
    { key: "tags", label: "Tags" },
    { key: "operations", label: "" },
  ];

  function renderRow(t) {
    const overdue = t.status === "in_progress" && isOverdue(t.due_date);
    const isMine = t.owner_id === user?.id;
    return (
      <tr key={t.id}>
        <td>{t.owner_name || "—"}</td>
        <td title={t.description}>
          {(t.description || "").substring(0, 60)}
          {(t.description || "").length > 60 ? "..." : ""}
        </td>
        <td>{t.responsable_name || "—"}</td>
        <td>
          {formatDate(t.due_date)}
          {overdue && <StatusBadge value="overdue" label="En retard" variant="danger" />}
        </td>
        <td>
          {t.stake_level && <StatusBadge value={t.stake_level} label={STAKE_LABELS[t.stake_level]} />}
        </td>
        <td>
          {t.urgency_level && <StatusBadge value={t.urgency_level} label={URGENCY_LABELS[t.urgency_level]} />}
        </td>
        <td>
          <StatusBadge value={t.status} label={STATUS_LABELS[t.status]} />
        </td>
        <td>
          <div className="tag-list">
            {t.tags?.map((tag) => (
              <span key={tag.id} className="tag-inline">{tag.label}</span>
            ))}
          </div>
        </td>
        <td className="row-actions">
          {isMine && (
            <>
              <button className="btn btn-small" onClick={() => navigate(`/todos/${t.id}/edit`)}>
                Modifier
              </button>
              <button className="btn btn-small btn-danger" onClick={() => setDeleteId(t.id)}>
                Supprimer
              </button>
            </>
          )}
        </td>
      </tr>
    );
  }

  if (!contact) return <div className="page"><p>Chargement...</p></div>;

  return (
    <div className="page">
      <div className="page-header">
        <h2>To Dos — {fullName || "Contact"}</h2>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          {isOwner && (
            <Link to={`/todos/new?contact_id=${id}`} className="btn btn-primary">
              Nouveau To Do
            </Link>
          )}
          <Link to={`/contacts/${id}`} className="btn btn-secondary">
            Retour au contact
          </Link>
        </div>
      </div>
      <SortableTable
        columns={columns}
        rows={todos}
        renderRow={renderRow}
        emptyMessage="Aucun To Do pour ce contact."
      />
      {deleteId && (
        <ConfirmDialog
          message="Supprimer ce To Do ?"
          onConfirm={handleDelete}
          onCancel={() => setDeleteId(null)}
        />
      )}
    </div>
  );
}
